import React, { useCallback, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';

interface FileUploadProps {
  projectId: string;
  onUploadComplete: (url: string, fileName: string, fileType: string) => void;
  onUploadError?: (error: Error) => void;
  acceptedTypes?: string;
  maxSizeMB?: number;
  label?: string;
  helperText?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  projectId,
  onUploadComplete,
  onUploadError,
  acceptedTypes = 'image/*,video/*,.pdf,.doc,.docx',
  maxSizeMB = 10,
  label = 'Upload Asset',
  helperText = 'Drag and drop or click to select one file (images, videos, PDFs, docs)',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        const errorMsg = `File size exceeds ${maxSizeMB}MB limit`;
        setError(errorMsg);
        if (onUploadError) onUploadError(new Error(errorMsg));
        return;
      }

      try {
        setUploading(true);
        setProgress(0);
        setFileName(file.name);

        // Create storage reference
        const timestamp = Date.now();
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `projects/${projectId}/uploads/${timestamp}_${sanitizedName}`;
        const storageRef = ref(storage, storagePath);

        // Upload file with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file, {
          contentType: file.type,
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const uploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setProgress(uploadProgress);
          },
          (error) => {
            console.error('Upload error:', error);
            setError(`Upload failed: ${error.message}`);
            setUploading(false);
            if (onUploadError) onUploadError(error);
          },
          async () => {
            // Upload complete - get download URL
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploading(false);
              setProgress(100);
              onUploadComplete(downloadURL, file.name, file.type);
            } catch (error: any) {
              console.error('Error getting download URL:', error);
              setError('Failed to get file URL');
              setUploading(false);
              if (onUploadError) onUploadError(error);
            }
          }
        );
      } catch (error: any) {
        console.error('File upload error:', error);
        setError(`Upload failed: ${error.message}`);
        setUploading(false);
        if (onUploadError) onUploadError(error);
      }
    },
    [projectId, maxSizeMB, onUploadComplete, onUploadError]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        if (e.dataTransfer.files.length > 1) {
          setError(`Only one file can be uploaded at a time. Using the first file: ${e.dataTransfer.files[0].name}`);
        }
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      if (e.target.files && e.target.files[0]) {
        handleFile(e.target.files[0]);
      }
    },
    [handleFile]
  );

  const clearFile = () => {
    setFileName(null);
    setProgress(0);
    setError(null);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200
          ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          type="file"
          onChange={handleChange}
          accept={acceptedTypes}
          disabled={uploading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        {!uploading && !fileName && (
          <div>
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">{helperText}</p>
            <p className="mt-1 text-xs text-gray-500">Max size: {maxSizeMB}MB</p>
          </div>
        )}

        {uploading && (
          <div>
            <div className="mx-auto h-12 w-12 relative">
              <svg className="animate-spin h-12 w-12 text-primary-500" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p className="mt-2 text-sm text-gray-600">Uploading {fileName}...</p>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">{Math.round(progress)}%</p>
          </div>
        )}

        {fileName && !uploading && progress === 100 && (
          <div>
            <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="mt-2 text-sm text-gray-600 font-medium">Uploaded: {fileName}</p>
            <button
              type="button"
              onClick={clearFile}
              className="mt-2 text-xs text-primary-600 hover:text-primary-700 underline"
            >
              Upload different file
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};
